<template>
  <fin-data-loader :resolver="fetchData" class="loader" @loaded="reflowChart">
    <template #content>
      <div class="demoToolContainer" v-if="activeAsset !== undefined" dense>
        <v-row
          :class="['mb-sm-3', 'pt-6', 'pb-0', {'fin-color-pale-grey fin-dialog-dense-x fin-dialog-dense-top': $vuetify.breakpoint.xs}]"
          dense>
          <v-col class="pb-6" cols="12">
            <h1>Kapitalmarkt schlägt Sparbuch</h1>
            <p class="mt-6">
              Finde heraus wie sich dein Geld am Kapitalmarkt im Vergleich zum Sparbuch entwickelt hätte.
            </p>
          </v-col>
        </v-row>

        <v-row
          :class="['pb-sm-5', 'flex-column', 'flex-md-row', {'fin-color-pale-grey triangle fin-dialog-dense-x fin-dialog-dense-top': $vuetify.breakpoint.xs} ]"
          dense>
          <v-col v-if="$vuetify.breakpoint.smAndUp" class="pt-5 pt-md-0">
            <h4>Anlagebetrag</h4>
            <v-slider v-model="seed_capital_display" :color="$finColors.primaryDark" :max="100000" :min="10000"
                      :track-color="$finColors.coolGrey" step="10000" style="align-items: center"
                      tick-size="4" ticks="always" @change="val => seed_capital=val">
              <template #append>
                <span style="min-width:100px; padding-left: 0.5em;">
                  {{ seed_capital_display | currency('€', 0) }}
                </span>
              </template>
            </v-slider>
          </v-col>

          <v-col class="pt-5 pt-md-0">
            <h4>Zeitraum</h4>
            <fin-range-selector
              v-model="timesSelected"
              :ranges="times"
              dropdown=""
            />
          </v-col>

          <v-col class="pt-5 pt-md-0">
            <h4>Assetklasse
              <fin-info-icon :alertTexts="assetDescription"></fin-info-icon>
            </h4>
            <v-select
              v-model="activeAsset"
              :items="assets"
              class="mb-n2"
              item-text="name"
              outlined
              return-object
              style="border-radius: unset; max-height: 40px;"
            />
          </v-col>

          <v-col v-if="$vuetify.breakpoint.xs" class="pt-5 pt-md-0">
            <h4 class="d-inline">Anlagebetrag: </h4>{{ seed_capital | currency('€', 0) }}
          </v-col>
        </v-row>

        <v-row class="pb-3" dense>
          <v-col class="justify-sm-center" cols="12" lg="6" md="6" sm="6" xl="6">
            <fin-demo-result :capital="seed_capital"
                             :result="passbook_result"
                             :time-text="selectedTime.altText"
                             :yield_pa_amount="passbook_yield_pa_amount"
                             color-class="fin-color-accent-blue">
              Anlage auf dem Sparbuch
              <fin-info-icon :alertTexts="passbookDescription"/>
            </fin-demo-result>
          </v-col>
          <v-col class="justify-sm-center" cols="12" lg="6" md="6" sm="6" xl="6">
            <fin-demo-result :capital="seed_capital"
                             :result="market_result"
                             :time-text="selectedTime.altText"
                             :yield_pa_amount="market_pa_amount"
                             color-class="fin-color-accent-green">
              Anlage am Kapitalmarkt
            </fin-demo-result>
          </v-col>
        </v-row>

        <v-row v-if="$vuetify.breakpoint.smAndUp" dense>
          <v-col :style="'background-color: white;'">
            <fin-chart2 ref="chart" :options="chartOptions"/>
          </v-col>
        </v-row>

        <v-row v-if="$vuetify.breakpoint.xs" ref="demoToolChartBtn" class="demoToolChartBtn" dense>
          <v-col cols="12">
            <v-btn v-if="showChartOnXS"
                   outlined
                   @click="showChartOnXS = false">
              Schaubild ausblenden
              <v-icon right>icon-chart</v-icon>
            </v-btn>
            <v-btn v-else
                   outlined
                   @click="showChartOnXS = true">
              Schaubild anzeigen
              <v-icon right>icon-chart</v-icon>
            </v-btn>
          </v-col>
          <v-col v-if="$vuetify.breakpoint.xs" cols="12">
            <div v-show="showChartOnXS" ref="demoToolChart" style="margin-bottom: 20px">
              <fin-chart2 ref="chart" :options="chartOptions"/>
            </div>
          </v-col>
        </v-row>
      </div>
      <div v-else>
        <v-row
          :class="['mb-sm-3', 'pt-6', 'pb-0', {'fin-color-pale-grey fin-dialog-dense-x fin-dialog-dense-top': $vuetify.breakpoint.xs}]"
          dense>
          <v-col class="pb-6" cols="12">
            <h1>Kapitalmarkt schlägt Sparbuch</h1>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col>
            <fin-error-msg msg="Ein Fehler ist aufgetreten. Versuch es bitte später nochmals."/>
          </v-col>
        </v-row>
      </div>
    </template>
  </fin-data-loader>
</template>

<script>
import {finDemoToolAPI} from '@/js/finAPI'
import FinDemoResult from "@/components/common/demoTool/DemoTool/DemoToolResult"
import FinInfoIcon from "../../InfoIcon/index"
import FinDataLoader from "@/components/common/DataLoader/index"
import FinChart2 from "@/components/common/Chart2/FinChart2"
import FinRangeSelector from "@/components/common/Chart2/RangeSelector"
import FinErrorMsg from "@/components/common/ErrorMsg"


export default {
  name: 'finDemoTool',
  components: {FinErrorMsg, FinRangeSelector, FinChart2, FinDataLoader, FinInfoIcon, FinDemoResult},
  data: () => ({
    activeAsset: null,
    assets: [],
    timesSelected: 4,
    showChartOnXS: false,
    times: [
      {
        type: 'year',
        count: 1,
        textDropDown: 'letztes Jahr',
        altText: 'einem Jahr',
      },
      {
        type: 'year',
        count: 2,
        textDropDown: 'letzte 2 Jahre',
        altText: '2 Jahren',
        dataGrouping: {
          forced: true,
          units: [['day', [1]]]
        }
      },
      {
        type: 'year',
        count: 3,
        textDropDown: 'letzte 3 Jahre',
        altText: '3 Jahren',
      },
      {
        type: 'year',
        count: 5,
        textDropDown: 'letzte 5 Jahre',
        altText: '5 Jahren',
      },
      {
        type: 'year',
        count: 10,
        textDropDown: 'letzte 10 Jahre',
        altText: '10 Jahren',
        dataGrouping: {
          forced: true,
          units: [['day', [1]]]
        }
      }
    ],
    seed_capital: 10000,
    seed_capital_display: 10000,
    passbook_result: null,
    passbook_yield_pa_amount: null,
    market_result: null,
    market_pa_amount: null,
    passbook_description: "",
    chartHeight: 375
  }),
  props: {
    readonly: {
      type: Boolean,
      default: false
    },
  },
  computed: {
    selectedTime() {
      return this.times[this.timesSelected]
    },
    chartOptions() {
      const instance = this
      let result = {
        preset: "investmentCompare",
        presetData: {
          investmentValue: this.seed_capital,
          currency: '€',
        },
        series: [
          {
            type: "area",
            name: this.$vuetify.breakpoint.smAndDown ? 'Kapitalmarkt' : 'Anlage am Kapitalmarkt',
            color: this.$finColors.accentGreen,
            data: this.activeAsset ? this.activeAsset.timeSeries : []
          },
          {
            type: "area",
            name: this.$vuetify.breakpoint.smAndDown ? 'Sparbuch' : 'Anlage auf dem Sparbuch',
            color: this.$finColors.accentBlue,
            data: this.activeAsset ? this.activeAsset.passbookSeries : [],
            marker: {
              symbol: 'circle'
            }
          }
        ],
        rangeSelector: {
          initSelection: 4,
          buttons: this.times,
        },
        chart: {
          events: {
            render({target}) {
              const years = instance.selectedTime.count
              //TODO pa per year
              const pData = target.series[1].groupedData
              const pStart = pData[0].y
              const pEnd = pData[pData.length - 1].y
              const pGrowth = 100 / pStart * pEnd

              instance.passbook_result = instance.seed_capital / 100 * pGrowth
              instance.passbook_yield_pa_amount = instance.yieldAnnualized(instance.seed_capital, instance.passbook_result, years)

              const mData = target.series[0].groupedData
              const mStart = mData[0].y
              const mEnd = mData[mData.length - 1].y
              const mGrowth = 100 / mStart * mEnd

              instance.market_result = instance.seed_capital / 100 * mGrowth
              instance.market_pa_amount = instance.yieldAnnualized(instance.seed_capital, instance.market_result, years)
            }
          }
        }
      }
      return result
    },
    assetDescription() {
      if (this.assets) {
        return {
          dialogWidth: 960,
          title: 'Folgende Indizes werden für die Performanceberechnung herangezogen',
          texts: this.assets.map(item => ({content: item.name + `: ` + item.description})),
        }
      }
      return null
    },
    passbookDescription() {
      let options = {
        texts: [
          {content: this.passbook_description}
        ]
      }
      return options
    }
  },

  methods: {
    yieldAnnualized(start, end, years) {
      //https://de.wikihow.com/Die-annualisierte-Portfoliorendite-berechnen
      const yield_all = (end - start) / start
      const result = Math.pow(1 + yield_all, 1 / years) - 1
      return result * 100
    },
    reflowChart() {
      if (this.$refs.chart) {
        this.$refs.chart.reflow()
      }

    },
    async fetchData() {
      const loadingSparzinsen = await finDemoToolAPI.getDemoToolSparplanzinsen()
      const loadingAssets = await finDemoToolAPI.getDemoToolAssets()

      const sparzinsen = await loadingSparzinsen
      const assets = await loadingAssets

      const sparplanzinsenSeries = sparzinsen.data.map(item => ({
        x: new Date(item.timestamp).getTime(),
        y: item.value / 100 / 365
      })).reverse()

      const getRate = function (day) {
        // sparplanzinsenSeries[0] contains the most current rate, sparplanzinsenSeries[n] the oldest rate
        // we try to find the first index with date lower than the given date
        let index = sparplanzinsenSeries.findIndex(sp => sp.x <= day)
        // in case no index was found, day < last entry => return oldest rate i.e. last index
        if (index < 0) {
          index = sparplanzinsenSeries.length - 1
        }

        return sparplanzinsenSeries[index].y
      }


      const msPerDay = 24 * 60 * 60 * 1000
      const getPassbookValue = function (seriesData, index) {
        const preDayIndex = index < 1 ? 0 : index - 1 //prevday or first
        const preDay = seriesData[preDayIndex][0]
        const currentDay = seriesData[index][0]
        const days = (currentDay - preDay) / msPerDay
        return getRate(preDay) * days
      }

      assets.forEach(asset => {
        asset.timeSeries = asset.data.map(item => ([
          new Date(item.timestamp).getTime(),
          item.value
        ]))
        let amount = 1
        asset.passbookSeries = asset.timeSeries.map(([date], index) => {
          amount += amount * getPassbookValue(asset.timeSeries, index)
          return [date, amount]
        })
      })

      this.passbook_description = sparzinsen.description
      this.assets = assets
      this.activeAsset = assets.length > 0 ? assets[0] : undefined
    }
  },
  watch: {
    showChartOnXS() {
      this.$nextTick(() => {
        this.$refs.demoToolChart?.scrollIntoView?.(false)
      })
    },
    timesSelected(val){
      this.$refs.chart.chart.rangeSelector.clickButton(val)
    }
  },
}
</script>

<style scoped lang="scss">
@import "../../../../styles/variables.scss";

.demoToolContainer {
  font-size: 14px;
}

h4 {
  font-size: 14px;
  padding-bottom: 5px;
}

::v-deep .v-list-item {
  height: 48px;
}

::v-deep .theme--light.v-text-field--outlined fieldset {
  min-height: 48px;
  height: 48px;
}

::v-deep .v-select.v-text-field--outlined:not(.v-text-field--single-line) .v-select__selections {
  margin-bottom: 14px;
  padding: unset !important;
}

::v-deep .v-text-field--enclosed .v-input__append-inner {
  margin-top: 10px;
}

::v-deep .theme--light.v-input input {
  padding: unset;
  margin-bottom: 14px;
}

::v-deep .v-application--is-ltr .v-list-item__action:first-child {
  margin-right: 30px !important;
}

/* Dropdown field */
::v-deep .v-text-field--outlined fieldset {
  border-width: 1px !important;
  background-color: white;
}

::v-deep .v-select__selection--comma {
  color: $fin-color-primary-dark !important;
}


/* Slider */

::v-deep .v-input__slider {

  .v-messages {
    min-height: 0;
  }

  .v-input__slot {
    margin: 0;
  }

}

/* Slider Handle */
::v-deep .v-slider__thumb:before {
  -webkit-transform: scale(0.8) !important;
  transform: scale(0.8) !important;
  color: #ffffff;
  box-shadow: $fin-color-shadow;
  border: 1px solid $fin-color-light-sky-blue;
}

::v-deep .v-slider__thumb-container .v-slider__thumb:before {
  opacity: 1;
  color: $fin-color-primary-light;
}

::v-deep .v-slider__thumb-container:hover .v-slider__thumb:before {
  opacity: 1;
  color: $fin-color-primary-light;
}

::v-deep .v-slider__thumb:after {
  content: "";
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: $fin-color-primary-dark;
  opacity: 1;
  position: absolute;
  left: 0;
  top: 0;
  pointer-events: none;
}

/* Sider Track size */
::v-deep .v-slider__track-container {
  height: 3px;
}

/*Upsize Silder Tick (value set to 4) & Style */
::v-deep .v-slider__tick {
  width: 2px !important;
  height: 8px !important;
  top: calc(50% - 5px) !important;
  background-color: $fin-color-accent-gray;
  border: 2px solid $fin-color-page-background;
  box-sizing: content-box;

  &:first-child, &:last-child {
    display: none;
  }
}

.triangle {
  position: relative;
  margin-bottom: 20px;
}

.triangle::after {
  content: '';
  position: absolute;
  left: 50%;
  transform: translate(-50%, 0);
  top: 100%;
  width: 0;
  height: 0;
  border-left: 20px solid transparent;
  border-right: 20px solid transparent;
  border-top: 15px solid $fin-color-pale-grey;
  clear: both;
}

.loader ::v-deep .loading {
  min-height: 800px;
}

::v-deep .highcharts-range-selector-group {
  display: none;
}
</style>
